import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { IEvent } from "../../../interfaces/models/IEvent";
import { updateEvent, getEventById } from "../../../Services/Event";
import { NewEvent } from "../../../redux/states/event.state";
import { toastPromise } from "../../../utils/toast";
import { uploadImage } from "../../../utils/UploadImage";
import PhotoThumbnail from "./PhotoThumbnail";
import ImageLightboxModal from "./ImageLightboxModal";
import DragAndDropGalleryUpload from "./DragAndDropGalleryUpload";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import ChevronRight from "../../../components/Atomos/Icons/ChevronRight";

type PhotosPerRow = 6 | 2;

function GridIcon2x2({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="1" y="1" width="10" height="10" rx="2" />
      <rect x="13" y="1" width="10" height="10" rx="2" />
      <rect x="1" y="13" width="10" height="10" rx="2" />
      <rect x="13" y="13" width="10" height="10" rx="2" />
    </svg>
  );
}

function GridIcon3x3({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="1" y="1" width="6" height="6" rx="1.5" />
      <rect x="9" y="1" width="6" height="6" rx="1.5" />
      <rect x="17" y="1" width="6" height="6" rx="1.5" />
      <rect x="1" y="9" width="6" height="6" rx="1.5" />
      <rect x="9" y="9" width="6" height="6" rx="1.5" />
      <rect x="17" y="9" width="6" height="6" rx="1.5" />
      <rect x="1" y="17" width="6" height="6" rx="1.5" />
      <rect x="9" y="17" width="6" height="6" rx="1.5" />
      <rect x="17" y="17" width="6" height="6" rx="1.5" />
    </svg>
  );
}

export default function StaffPhotoGalleryTab({
  event,
  isEditMode,
  onEditClick,
  onEventUpdate,
}: {
  event: IEvent | null;
  isEditMode: boolean;
  onEditClick: () => void;
  onEventUpdate?: (updatedEvent: IEvent) => void;
}) {
  const dispatch = useDispatch();

  const [lightbox, setLightbox] = useState<{
    isOpen: boolean;
    imageSrc: string | null;
    index: number;
  }>({ isOpen: false, imageSrc: null, index: 0 });
  const [photosPerRow, setPhotosPerRow] = useState<PhotosPerRow>(6);
  const [localPhotoGallery, setLocalPhotoGallery] = useState<(string | File)[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const objectURLsRef = useRef<Map<File, string>>(new Map());

  const getDisplayUrl = useCallback((item: string | File): string => {
    if (typeof item === "string") {
      return item;
    }
    let url = objectURLsRef.current.get(item);
    if (!url) {
      url = URL.createObjectURL(item);
      objectURLsRef.current.set(item, url);
    }
    return url;
  }, []);

  useEffect(() => {
    const currentFiles = new Set(
      localPhotoGallery.filter((x): x is File => x instanceof File)
    );
    for (const [file, url] of objectURLsRef.current.entries()) {
      if (!currentFiles.has(file)) {
        URL.revokeObjectURL(url);
        objectURLsRef.current.delete(file);
      }
    }
  }, [localPhotoGallery]);

  const photosForDisplay =
    isEditMode
      ? localPhotoGallery.map((item) => getDisplayUrl(item))
      : (event?.photoGallery ?? []);
  const photos = photosForDisplay;

  useEffect(() => {
    if (isEditMode && event) {
      setLocalPhotoGallery((event.photoGallery ?? []) as (string | File)[]);
    }
  }, [isEditMode, event]);

  const openLightbox = useCallback((src: string, index: number) => {
    setLightbox({ isOpen: true, imageSrc: src, index });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox({ isOpen: false, imageSrc: null, index: 0 });
  }, []);

  const lightboxPrevious = useCallback(() => {
    setLightbox((prev) => {
      if (prev.index <= 0 || !prev.imageSrc) {
        return prev;
      }
      const newIndex = prev.index - 1;
      const newSrc = photos[newIndex];
      return newSrc != null ? { ...prev, index: newIndex, imageSrc: newSrc } : prev;
    });
  }, [photos]);

  const lightboxNext = useCallback(() => {
    setLightbox((prev) => {
      if (prev.index >= photos.length - 1 || !prev.imageSrc) {
        return prev;
      }
      const newIndex = prev.index + 1;
      const newSrc = photos[newIndex];
      return newSrc != null ? { ...prev, index: newIndex, imageSrc: newSrc } : prev;
    });
  }, [photos]);

  const handleRemovePhoto = useCallback((index: number) => {
    setLocalPhotoGallery((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleFilesAdded = useCallback((files: File[]) => {
    setLocalPhotoGallery((prev) => [...prev, ...files]);
  }, []);

  const reorderPhotos = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) {
      return;
    }
    setLocalPhotoGallery((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    const eventId = event?.id;
    if (!eventId || isSaving) {
      return;
    }
    const sanitizeFileName = (fileName: string) =>
      fileName
        .toLowerCase()
        .replace(/[^a-z0-9.]/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");

    const savePromise = (async () => {
      const finalUrls: string[] = [];
      for (const item of localPhotoGallery) {
        if (typeof item === "string") {
          finalUrls.push(item);
        } else {
          const sanitized = sanitizeFileName(item.name);
          const sanitizedFile = new File([item], sanitized, { type: item.type });
          const url = await uploadImage(sanitizedFile, eventId, "events/");
          if (!url) {
            throw new Error("Failed to upload one or more images");
          }
          finalUrls.push(url);
        }
      }

      await updateEvent({
        ...event,
        photoGallery: finalUrls,
      });
      const reloadedEvent = await getEventById(eventId);
      if (reloadedEvent) {
        dispatch(NewEvent(reloadedEvent));
        onEventUpdate?.(reloadedEvent);
        onEditClick();
      }
      return reloadedEvent;
    })();

    setIsSaving(true);
    try {
      await toastPromise(savePromise, {
        pending: "Uploading and saving photo gallery...",
        success: "Photo gallery updated successfully",
        error: "Error updating photo gallery",
      });
    } finally {
      setIsSaving(false);
    }
  }, [event, localPhotoGallery, dispatch, onEventUpdate, onEditClick, isSaving]);

  if (!event) {
    return <div className="p-8 text-center text-gray-500">Event not found</div>;
  }

  const itemFlexBasis =
    photosPerRow === 6
      ? "calc((100% - 5 * 1rem) / 6)"
      : "calc((100% - 1rem) / 2)";

  return (
    <div className="">
      <div
        className={`mb-4 flex items-center ${
          photos.length > 0 ? "justify-end" : ""
        }`}
      >
        {photos.length > 0 && (
          <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-100 p-0.5">
            <button
              type="button"
              onClick={() => setPhotosPerRow(2)}
              className={`rounded-md p-1.5 transition-colors ${photosPerRow === 2 ? "bg-secondary text-white" : "text-gray-600 hover:bg-gray-200 hover:text-gray-900"}`}
              aria-label="Show 2 photos per row"
            >
              <GridIcon2x2 className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => setPhotosPerRow(6)}
              className={`rounded-md p-1.5 transition-colors ${photosPerRow === 6 ? "bg-secondary text-white" : "text-gray-600 hover:bg-gray-200 hover:text-gray-900"}`}
              aria-label="Show 6 photos per row"
            >
              <GridIcon3x3 className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {photos.length === 0 && !isEditMode && (
        <div className="text-center py-12 text-gray-500">
          No photos available for this event.
        </div>
      )}

      {(photos.length > 0 || isEditMode) && (
        <>
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-4 mb-6">
              {(isEditMode ? localPhotoGallery : (event?.photoGallery ?? [])).map((item, index) => {
                const src = isEditMode ? getDisplayUrl(item as string | File) : (item as string);
                const key = typeof item === "string" ? item : `file-${index}-${(item as File).name}-${(item as File).size}`;
                return (
                  <div
                    key={key}
                    className={`flex-grow-0 flex-shrink-0 min-w-0 transition-[flex-basis,box-shadow,opacity] duration-300 ease-in-out
                      ${isEditMode && photos.length > 1 ? "cursor-grab active:cursor-grabbing" : ""}
                      ${draggedIndex === index ? "opacity-50" : ""}
                      ${dragOverIndex === index && draggedIndex !== null ? "ring-2 ring-secondary ring-offset-2 rounded-lg" : ""}`}
                    style={{ flexBasis: itemFlexBasis }}
                    draggable={isEditMode && photos.length > 1}
                    onDragStart={(e) => {
                      if (!isEditMode || photos.length <= 1) {
                        return;
                      }
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", String(index));
                      setDraggedIndex(index);
                    }}
                    onDragEnd={() => {
                      setDraggedIndex(null);
                      setDragOverIndex(null);
                    }}
                    onDragOver={(e) => {
                      if (draggedIndex === null) {
                        return;
                      }
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      setDragOverIndex(index);
                    }}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                        setDragOverIndex(null);
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = Number(e.dataTransfer.getData("text/plain"));
                      const to = index;
                      if (from !== to && !Number.isNaN(from)) {
                        reorderPhotos(from, to);
                      }
                      setDraggedIndex(null);
                      setDragOverIndex(null);
                    }}
                  >
                    <PhotoThumbnail
                      src={src}
                      alt={`Photo ${index + 1}`}
                      onClick={() => openLightbox(src, index)}
                      onRemove={
                        isEditMode
                          ? () => handleRemovePhoto(index)
                          : undefined
                      }
                    />
                  </div>
                );
              })}
            </div>
          )}

          {isEditMode && (
            <>
              <div className="mb-6">
                <DragAndDropGalleryUpload
                  onFilesAdded={handleFilesAdded}
                />
              </div>
              <div className="flex justify-end">
                <LegacyButton
                  content="textIcon"
                  text="SUBMIT"
                  icon={<ChevronRight className="w-5 h-5" />}
                  onClick={handleSave}
                  color="accent"
                  size="small"
                  disabled={isSaving}
                />
              </div>
            </>
          )}
        </>
      )}

      <ImageLightboxModal
        isOpen={lightbox.isOpen}
        imageSrc={lightbox.imageSrc}
        onClose={closeLightbox}
        currentIndex={lightbox.index}
        totalCount={photos.length}
        onPrevious={lightboxPrevious}
        onNext={lightboxNext}
      />
    </div>
  );
}
